import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "./api";
import { useAuth } from "./auth";
import type { Flat } from "./types";

type ActiveFlatState =
  | { status: "loading"; flat: null }
  | { status: "loaded"; flat: Flat }
  | { status: "none"; flat: null };

type Ctx = ActiveFlatState & {
  reload: () => Promise<void>;
  setFlat: (flat: Flat) => void;
};

const ActiveFlatContext = createContext<Ctx | null>(null);
const STORAGE_KEY = "flat:active";

export function ActiveFlatProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [state, setState] = useState<ActiveFlatState>({ status: "loading", flat: null });

  const load = async () => {
    if (auth.status !== "authed") {
      setState({ status: "none", flat: null });
      return;
    }

    const me = await api
      .get<{ flats: { id: string }[] }>("/auth/me")
      .catch(() => null);
    const flats = me?.flats ?? [];
    if (flats.length === 0) {
      setState({ status: "none", flat: null });
      return;
    }

    const storedId = localStorage.getItem(STORAGE_KEY);
    const target = flats.find((f) => f.id === storedId) ?? flats[0]!;

    try {
      const flat = await api.get<Flat>(`/flat/${target.id}`);
      localStorage.setItem(STORAGE_KEY, flat.id);
      setState({ status: "loaded", flat });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        localStorage.removeItem(STORAGE_KEY);
        setState({ status: "none", flat: null });
        return;
      }
      throw err;
    }
  };

  useEffect(() => {
    if (auth.status === "loading") return;
    setState({ status: "loading", flat: null });
    load().catch(() => setState({ status: "none", flat: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.status, auth.user?.id]);

  const setFlat = (flat: Flat) => {
    localStorage.setItem(STORAGE_KEY, flat.id);
    setState({ status: "loaded", flat });
  };

  const reload = async () => {
    await load();
  };

  return (
    <ActiveFlatContext.Provider value={{ ...state, reload, setFlat }}>
      {children}
    </ActiveFlatContext.Provider>
  );
}

export function useActiveFlat(): Ctx {
  const ctx = useContext(ActiveFlatContext);
  if (!ctx) throw new Error("useActiveFlat must be used inside <ActiveFlatProvider>");
  return ctx;
}

export function useRequiredFlat(): { flat: Flat; reload: () => Promise<void>; setFlat: (flat: Flat) => void } {
  const { flat, reload, setFlat } = useActiveFlat();
  if (!flat) throw new Error("useRequiredFlat called before flat was loaded");
  return { flat, reload, setFlat };
}
